// Benchmark — the six marks (front-end redesign, 04/08/2026).
//
// HANDOFF §2: "Six, on one 20×20 grid, 2px stroke, round caps, node radius 2.6.
// Each has a different silhouette so shape does the work at 17px … Colour is
// never the only difference — every mark reads with the orange removed." These
// six ARE the whole icon set. Do not add a seventh; a seventh starts an icon
// library, "and then it stops looking like ASP."
//
// Geometry below reproduces the design marks exactly. Colours are token-only
// (§1.8 "Nothing outside tokens/colors.css"): three palettes swap the same
// geometry —
//   colour  navy strokes, orange accent, warm-grey secondary   (default)
//   mono    everything dark blue — the "orange removed" reading (§2)
//   white   white on a navy field (meeting mode / Office Display), orange kept
//
// Every mark is decorative by default (aria-hidden). Pass { label } to give it
// an accessible name (role="img" + <title>).

const PALETTE = {
  colour: { primary: 'var(--anchor)', accent: 'var(--brand-orange)', secondary: 'var(--hair-strong)' },
  mono:   { primary: 'var(--anchor)', accent: 'var(--anchor)', secondary: 'var(--anchor)' },
  white:  { primary: 'var(--brand-white)',     accent: 'var(--brand-orange)',    secondary: 'var(--brand-white)' },
};

// Each mark is a function of the palette, returning the inner SVG geometry.
// Silhouettes: horizontal pair · vertical spine · circle · broken diagonal ·
// T on a ground rule · closed box.
const GEOMETRY = {
  // Golden thread — two records joined. Project header button, client card.
  'golden-thread': (p) => `
    <circle cx="4.6" cy="10" r="2.6" fill="${p.accent}"/>
    <path d="M7.8 10 H12.2" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <circle cx="15.4" cy="10" r="2.6" fill="${p.primary}"/>`,

  // Instruction — a project branching to an instruction. Instructions sections,
  // timesheet grid header.
  instruction: (p) => `
    <path d="M5 4.4 V15.6" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <path d="M5 7.6 H10.2" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <circle cx="13.4" cy="7.6" r="2.6" fill="${p.primary}"/>
    <path d="M5 14 H10.2" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <circle cx="13.4" cy="14" r="2.6" fill="${p.accent}"/>`,

  // Time — hours as bars per day; the only circle. Log time, week total.
  time: (p) => `
    <circle cx="10" cy="10" r="7" stroke="${p.primary}" stroke-width="2" fill="none"/>
    <path d="M10 10 V5.6" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <path d="M10 10 H13.8" stroke="${p.accent}" stroke-width="2" stroke-linecap="round"/>`,

  // Issue — a rule broken on the diagonal. Log issue.
  issue: (p) => `
    <path d="M4.4 5.8 L8.2 9" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <path d="M11.8 11 L15.6 14.2" stroke="${p.secondary}" stroke-width="2" stroke-linecap="round"/>
    <circle cx="10" cy="10" r="2.8" fill="${p.accent}"/>`,

  // Site visit — a node standing on the ground rule. Site visit.
  'site-visit': (p) => `
    <circle cx="10" cy="5.4" r="2.8" fill="${p.accent}"/>
    <path d="M10 8.8 V15" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <path d="M4.4 17 H15.6" stroke="${p.secondary}" stroke-width="2" stroke-linecap="round"/>`,

  // Invoice — three rules in a closed box; the only closed shape. Raise
  // invoice, purchase orders.
  invoice: (p) => `
    <rect x="4" y="4" width="12" height="12" rx="2.6" stroke="${p.primary}" stroke-width="2" fill="none"/>
    <path d="M7.4 8.6 H12.6" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <path d="M7.4 12.4 H12.6" stroke="${p.accent}" stroke-width="2" stroke-linecap="round"/>`,
};

export const MARK_NAMES = Object.keys(GEOMETRY);

/**
 * Return an inline SVG string for one of the six marks.
 * @param {string} name  one of MARK_NAMES
 * @param {object} [opts]
 * @param {'colour'|'mono'|'white'} [opts.variant='colour']
 * @param {number} [opts.size=20]
 * @param {string} [opts.className]
 * @param {string} [opts.label]  accessible name; omit for decorative
 */
export function mark(name, opts = {}) {
  const geom = GEOMETRY[name];
  if (!geom) throw new Error(`Unknown mark "${name}" — the set is fixed at six (${MARK_NAMES.join(', ')}).`);
  const { variant = 'colour', size = 20, className = '', label } = opts;
  const p = PALETTE[variant] || PALETTE.colour;
  const cls = `mark${className ? ' ' + className : ''}`;
  const a11y = label
    ? `role="img" aria-label="${label}"><title>${label}</title`
    : `aria-hidden="true"`;
  return `<svg class="${cls}" width="${size}" height="${size}" viewBox="0 0 20 20" fill="none" ${a11y}>${geom(p)}</svg>`;
}

/**
 * The time-bars data-viz glyph — three day-bars on a baseline. This is NOT one
 * of the six marks; it is the "hours as bars per day" reading used inside the
 * Time figures, distinct from the Time mark (the clock).
 */
export function timeBars(opts = {}) {
  const { variant = 'colour', size = 20, className = '' } = opts;
  const p = PALETTE[variant] || PALETTE.colour;
  const cls = `mark mark-timebars${className ? ' ' + className : ''}`;
  return `<svg class="${cls}" width="${size}" height="${size}" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M2.5 14.5 H15.5" stroke="${p.secondary}" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M5 14.5 V9.5" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <path d="M9 14.5 V4.5" stroke="${p.primary}" stroke-width="2" stroke-linecap="round"/>
    <path d="M13 14.5 V11.5" stroke="${p.accent}" stroke-width="2" stroke-linecap="round"/>
  </svg>`;
}
